import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bulkUpdateMemberRunnerModelApproval,
  createMemberRunnerModel,
  deleteMemberRunnerModel,
  fetchMemberRunnerModels,
  updateMemberRunnerModel,
  type CreateMemberRunnerModelInput,
  type BulkUpdateMemberRunnerModelApprovalInput,
  type MemberRunnerModelQueryParams,
  type UpdateMemberRunnerModelInput,
} from '../../lib/api';
import { queryKeys } from './queryKeys';

export const useMemberRunnerModels = (token: string | null, params?: MemberRunnerModelQueryParams) => {
  return useQuery({
    queryKey: queryKeys.memberRunnerModels.list(params ?? {}),
    queryFn: () => fetchMemberRunnerModels(token as string, params),
    enabled: Boolean(token),
    staleTime: 300_000,
    placeholderData: keepPreviousData,
  });
};

export const useCreateMemberRunnerModelMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateMemberRunnerModelInput) => createMemberRunnerModel(token as string, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberRunnerModels.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.runnerModels.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.runnerRequestBootstrap.root });
    },
  });
};

export const useUpdateMemberRunnerModelMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateMemberRunnerModelInput }) =>
      updateMemberRunnerModel(token as string, id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberRunnerModels.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.runnerModels.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.runnerRequestBootstrap.root });
    },
  });
};

export const useBulkUpdateMemberRunnerModelApprovalMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: BulkUpdateMemberRunnerModelApprovalInput) =>
      bulkUpdateMemberRunnerModelApproval(token as string, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberRunnerModels.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.runnerModels.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.runnerRequestBootstrap.root });
    },
  });
};

export const useDeleteMemberRunnerModelMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteMemberRunnerModel(token as string, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberRunnerModels.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.runnerModels.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.runnerRequestBootstrap.root });
    },
  });
};
